import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  Row,
  Col,
  message,
} from 'antd';
import {
  PlayCircleOutlined,
  ExperimentOutlined,
  HomeOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import MainLayout from '../../components/common/MainLayout';
import AppModeCards from '../../components/navigation/AppModeCards';
import {
  useCreateFreeSaveMutation,
  useDeleteSaveMutation,
  useListSavesQuery,
  useListSystemWeatherMapsQuery,
} from '../../api/savesV2Api';
import type { Save, SaveStatus, SaveType } from '../../types/saves';
import type { WeatherMapItem } from '../../types/simulation';
import GuidedSaveCreationWizard from '../../components/saves/GuidedSaveCreationWizard';

const { Title, Text } = Typography;

const SAVE_TYPE_META: Record<SaveType, { label: string; color: string; icon: React.ReactNode }> = {
  free: { label: 'Свободный сейв', color: 'green', icon: <HomeOutlined /> },
  scenario: { label: 'Сценарий', color: 'orange', icon: <ExperimentOutlined /> },
};

const STATUS_META: Record<string, { label: string; color: string }> = {
  active: { label: 'Активен', color: 'blue' },
  completed: { label: 'Завершён', color: 'green' },
  archived: { label: 'В архиве', color: 'default' },
  requires_migration: { label: 'Нужна миграция', color: 'red' },
};

type TypeFilter = 'all' | SaveType;
type StatusFilter = 'active' | 'all' | SaveStatus;

const formatDate = (iso: string | null | undefined, fallback = '—') =>
  iso ? dayjs(iso).format('DD.MM.YYYY HH:mm') : fallback;

const formatSimDate = (cursor: Save['cursor']) =>
  cursor?.sim_date ? dayjs(cursor.sim_date).format('DD.MM.YYYY') : '—';

const DEFAULT_SAVE_NAME = 'Мой сейв';
const DEFAULT_FIELD_NAME = 'Моё поле';
const DEFAULT_PLOT_NAME = 'Мой участок';
const MEASUREMENT_OPTIONS = [
  { label: 'Метры', value: 'metric' },
  { label: 'Футы', value: 'imperial' },
];

const metadataOf = (map: WeatherMapItem | null | undefined): Record<string, unknown> => (
  (map?.metadata || {}) as Record<string, unknown>
);

const coordsFromWeatherMap = (map: WeatherMapItem | null | undefined): string | undefined => {
  const meta = metadataOf(map);
  const lat = Number(meta.lat);
  const lon = Number(meta.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return undefined;
  return `${lat},${lon}`;
};

const buildWeatherMapOptions = (maps: WeatherMapItem[]) => {
  const groups = new Map<string, { label: string; options: { label: string; value: string }[] }>();
  maps.forEach((map) => {
    const meta = metadataOf(map);
    const groupId = String(meta.regionGroup || 'system');
    const groupLabel = String(meta.regionGroupRu || 'Предустановленные карты');
    if (!groups.has(groupId)) {
      groups.set(groupId, { label: groupLabel, options: [] });
    }
    const country = meta.countryRu ? `, ${meta.countryRu}` : '';
    const koppen = meta.koppen ? ` · ${meta.koppen}` : '';
    const regionName = meta.regionRu ? String(meta.regionRu) : map.name.replace(' — предустановленная карта', '');
    const variant = meta.yearTypeRu ? ` · ${meta.yearTypeRu}` : '';
    groups.get(groupId)?.options.push({
      value: map.id,
      label: `${regionName}${variant}${country}${koppen}`,
    });
  });
  return Array.from(groups.values());
};

const SaveCard: React.FC<{
  save: Save;
  deleting: boolean;
  onDelete: (save: Save) => void;
}> = ({ save, deleting, onDelete }) => {
  const navigate = useNavigate();
  const typeMeta = SAVE_TYPE_META[save.type] ?? { label: save.type, color: 'default', icon: null };
  const statusMeta = STATUS_META[save.status] ?? { label: save.status, color: 'default' };
  const tier = save.capabilitiesSnapshot?.tier;
  const canDelete = save.status !== 'archived';

  return (
    <Card
      hoverable
      onClick={() => navigate(`/saves/${save.id}`)}
      style={{ height: '100%' }}
      title={
        <Space>
          <span>{typeMeta.icon}</span>
          <Tag color={typeMeta.color}>{typeMeta.label}</Tag>
          <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
          {tier ? <Tag color="purple">{`Tier: ${tier}`}</Tag> : null}
        </Space>
      }
      extra={
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            type="link"
            onClick={() => navigate(`/saves/${save.id}`)}
          >
            Открыть
          </Button>
          {canDelete ? (
            <Popconfirm
              title="Удалить сейв?"
              description="Сейв будет перенесён в архив."
              okText="Удалить"
              okType="danger"
              cancelText="Отмена"
              onConfirm={() => onDelete(save)}
            >
              <Button
                size="small"
                type="link"
                danger
                icon={<DeleteOutlined />}
                loading={deleting}
              >
                Удалить
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">ID: </Text>
          <Text code copyable={{ text: save.id }}>
            {save.id.slice(0, 8)}…
          </Text>
        </div>
        <div>
          <Text type="secondary">Текущая дата симуляции: </Text>
          <Text strong>{formatSimDate(save.cursor)}</Text>
        </div>
        {save.measurementSystem ? (
          <div>
            <Text type="secondary">Масштаб: </Text>
            <Text>
              {save.measurementSystem === 'imperial' ? 'Футы' : 'Метры'}
              {save.planScale?.display?.cellSize ? ` · ${save.planScale.display.cellSize}` : ''}
            </Text>
          </div>
        ) : null}
        {save.type === 'free' && save.farmId ? (
          <div>
            <Text type="secondary">Ферма-контейнер: </Text>
            <Text>{save.farmName ? `${save.farmName} ` : ''}</Text>
            <Text code>{save.farmId.slice(0, 8)}…</Text>
          </div>
        ) : null}
        {save.type === 'scenario' && save.scenarioTemplateId ? (
          <div>
            <Text type="secondary">Сценарий: </Text>
            <Text code>{save.scenarioTemplateId.slice(0, 8)}…</Text>
            {save.scenarioTemplateVersion != null ? (
              <Text type="secondary"> v{save.scenarioTemplateVersion}</Text>
            ) : null}
          </div>
        ) : null}
        <div>
          <Text type="secondary">Cache policy: </Text>
          <Text>{save.cachePolicy}</Text>
        </div>
        <div>
          <Text type="secondary">Engine: </Text>
          <Text>{save.engineVersion}</Text>
          <Text type="secondary"> · revision </Text>
          <Text>{String(save.revision)}</Text>
        </div>
        <div>
          <Text type="secondary">Последний запуск: </Text>
          <Text>{formatDate(save.lastPlayedAt, 'не запускался')}</Text>
        </div>
        <div>
          <Text type="secondary">Создан: </Text>
          <Text>{formatDate(save.createdAt)}</Text>
        </div>
      </Space>
    </Card>
  );
};

export default function SavesListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [form] = Form.useForm();
  const selectedWeatherSeriesId = Form.useWatch('weatherSeriesId', form);
  const {
    data: weatherMaps = [],
    isError: weatherMapsError,
    isFetching: weatherMapsFetching,
    isLoading: weatherMapsLoading,
  } = useListSystemWeatherMapsQuery(undefined, {
    skip: !createModalOpen,
  });
  const [createFreeSave, { isLoading: creatingFreeSave }] = useCreateFreeSaveMutation();
  const [deleteSave] = useDeleteSaveMutation();
  const [deletingSaveId, setDeletingSaveId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    const status = params.get('status');
    if (type === 'free' || type === 'scenario') setTypeFilter(type);
    if (status === 'active' || status === 'completed' || status === 'archived' || status === 'requires_migration' || status === 'all') {
      setStatusFilter(status as StatusFilter);
    }
  }, [location.search]);

  const queryArgs = useMemo(() => {
    const args: { type?: SaveType; status?: SaveStatus; limit?: number } = { limit: 100 };
    if (typeFilter !== 'all') args.type = typeFilter;
    if (statusFilter !== 'all') args.status = statusFilter as SaveStatus;
    return args;
  }, [typeFilter, statusFilter]);

  const { data: saves, isLoading, isError, refetch } = useListSavesQuery(queryArgs);

  const selectedWeatherMap = useMemo(
    () => weatherMaps.find((map) => map.id === selectedWeatherSeriesId) || null,
    [weatherMaps, selectedWeatherSeriesId],
  );

  const weatherMapOptions = useMemo(() => buildWeatherMapOptions(weatherMaps), [weatherMaps]);

  useEffect(() => {
    if (!createModalOpen || !weatherMaps[0]) return;
    const current = form.getFieldValue('weatherSeriesId');
    if (!current) {
      form.setFieldsValue({
        weatherSeriesId: weatherMaps[0].id,
        location_coords: coordsFromWeatherMap(weatherMaps[0]),
      });
    }
  }, [createModalOpen, form, weatherMaps]);

  const openCreateModal = () => {
    form.setFieldsValue({
      name: DEFAULT_SAVE_NAME,
      startDate: dayjs(),
      measurementSystem: 'metric',
      fieldName: DEFAULT_FIELD_NAME,
      plotName: DEFAULT_PLOT_NAME,
    });
    setCreateModalOpen(true);
  };

  const handleWeatherChange = (weatherSeriesId: string) => {
    const selected = weatherMaps.find((map) => map.id === weatherSeriesId);
    form.setFieldsValue({ location_coords: coordsFromWeatherMap(selected) });
  };

  const handleCreateFreeSave = async () => {
    try {
      const values = await form.validateFields();
      const startDate = values.startDate?.format
        ? values.startDate.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
      const payload: Record<string, unknown> = {
        name: values.name || DEFAULT_SAVE_NAME,
        description: values.description || '',
        startDate,
        measurementSystem: values.measurementSystem || 'metric',
        location_coords: values.location_coords || coordsFromWeatherMap(selectedWeatherMap),
        fieldName: DEFAULT_FIELD_NAME,
        plotName: DEFAULT_PLOT_NAME,
      };
      if (values.weatherSeriesId) {
        payload.weatherSeriesId = values.weatherSeriesId;
      }
      const result = await createFreeSave(payload).unwrap();
      form.resetFields();
      setCreateModalOpen(false);
      const saveId = result?.save?.id;
      if (saveId) {
        navigate(`/saves/${saveId}`);
      } else {
        message.success('Сейв создан');
        refetch();
      }
    } catch (err: any) {
      if (!err?.errorFields) {
        const e = err as { data?: { message?: string } };
        message.error(e?.data?.message || 'Не удалось создать сейв');
      }
    }
  };

  const handleDeleteSave = async (save: Save) => {
    setDeletingSaveId(save.id);
    try {
      await deleteSave(save.id).unwrap();
      message.success('Сейв удалён');
      refetch();
    } catch (err) {
      const e = err as { data?: { message?: string } };
      message.error(e?.data?.message || 'Не удалось удалить сейв');
    } finally {
      setDeletingSaveId(null);
    }
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">Главная</Link> },
            { title: 'Сейвы' },
          ]}
        />
      }
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <PlayCircleOutlined /> Игровые сейвы
        </Title>
        <Space wrap>
          <Button type="primary" icon={<HomeOutlined />} onClick={() => setWizardOpen(true)}>
            Создать сейв
          </Button>
          <Button icon={<HomeOutlined />} onClick={openCreateModal}>
            Быстрое создание
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={() => navigate('/scenarios')}
          >
            Запустить сценарий
          </Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Игровые сейвы"
        description="Сейв — основной игровой объект. Свободный сейв имеет одну привязанную ферму-контейнер для плана, доступа и объектов мира."
      />

      <div style={{ marginBottom: 16 }}>
        <AppModeCards compact />
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Space>
          <Text type="secondary">Тип:</Text>
          <Segmented
            value={typeFilter}
            onChange={(v) => setTypeFilter(v as TypeFilter)}
            options={[
              { label: 'Все', value: 'all' },
              { label: 'Свободные', value: 'free' },
              { label: 'Сценарии', value: 'scenario' },
            ]}
          />
        </Space>
        <Space>
          <Text type="secondary">Статус:</Text>
          <Segmented
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            options={[
              { label: 'Активные', value: 'active' },
              { label: 'Завершённые', value: 'completed' },
              { label: 'Архив', value: 'archived' },
              { label: 'Все', value: 'all' },
            ]}
          />
        </Space>
        <Button onClick={() => refetch()}>Обновить</Button>
      </Space>

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Не удалось загрузить список сейвов"
        />
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : !saves || saves.length === 0 ? (
        <Empty description="Сейвов не найдено" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Space>
            <Button type="primary" onClick={() => setWizardOpen(true)}>Создать сейв</Button>
            <Button onClick={() => navigate('/scenarios')}>Открыть каталог сценариев</Button>
          </Space>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {saves.map((save) => (
            <Col key={save.id} xs={24} md={12} xl={8}>
              <SaveCard
                save={save}
                deleting={deletingSaveId === save.id}
                onDelete={handleDeleteSave}
              />
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title="Новый свободный сейв"
        open={createModalOpen}
        onOk={handleCreateFreeSave}
        onCancel={() => setCreateModalOpen(false)}
        okText="Создать"
        cancelText="Отмена"
        confirmLoading={creatingFreeSave}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Название сейва" name="name" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Погодная карта"
            name="weatherSeriesId"
          >
            <Select
              allowClear
              showSearch
              loading={weatherMapsLoading || weatherMapsFetching}
              options={weatherMapOptions}
              optionFilterProp="label"
              onChange={handleWeatherChange}
              placeholder="Автоматически выбрать ближайшую"
              notFoundContent={
                weatherMapsLoading || weatherMapsFetching
                  ? <Spin size="small" />
                  : weatherMapsError
                    ? 'Не удалось загрузить карты'
                    : 'Backend выберет ближайшую карту'
              }
            />
          </Form.Item>
          {selectedWeatherMap ? (
            <Text type="secondary">
              {String(metadataOf(selectedWeatherMap).regionRu || selectedWeatherMap.name)} · {String(metadataOf(selectedWeatherMap).koppen || 'климат')}
            </Text>
          ) : (
            <Text type="secondary">
              Можно оставить пустым: при создании будет выбрана ближайшая системная погодная карта.
            </Text>
          )}
          <Form.Item
            label="Система измерений"
            name="measurementSystem"
            rules={[{ required: true, message: 'Выберите систему измерений' }]}
            style={{ marginTop: 16 }}
          >
            <Segmented options={MEASUREMENT_OPTIONS} block />
          </Form.Item>
          <Text type="secondary">
            Метры: клетка 0.5 x 0.5 m. Футы: клетка 2 x 2 ft. После создания изменить нельзя.
          </Text>
          <Form.Item label="Дата старта" name="startDate" rules={[{ required: true, message: 'Выберите дату старта' }]} style={{ marginTop: 16 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="location_coords" hidden>
            <Input />
          </Form.Item>
          <Form.Item label="Описание" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <GuidedSaveCreationWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(saveId) => navigate(`/saves/${saveId}`)}
      />
    </MainLayout>
  );
}
